import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import {
  Plus, X, CaretLeft, CaretRight, Coins, Handshake, MapPin, Truck as TruckIcon, Trash, Calendar, Stack,
} from "@phosphor-icons/react";
import { LanesManager } from "./Customers";
import { ReconcileCorrectionNote } from "../components/ReconcileCorrectionNote";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FX = { GHS: "GH₵", XOF: "CFA", USD: "$", EUR: "€" };

// Helpers
function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function mondayOf(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isoWeek(d) {
  const t = new Date(d);
  t.setHours(0, 0, 0, 0);
  t.setDate(t.getDate() + 4 - (t.getDay() || 7));
  const yearStart = new Date(t.getFullYear(), 0, 1);
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
}
function fmtMoney(n, cur) { return `${FX[cur] || cur} ${Math.round(n).toLocaleString()}`; }

export default function Contracts() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contracts", { params: showArchived ? { include_archived: true } : {} });
      setList(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load contracts");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  return (
    <div data-testid="contracts-page">
      <PageHeader
        subtitle="Recurring customers · Weekly trip ledger"
        title="Contracts"
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowArchived((v) => !v)}
              data-testid="contracts-show-archived-toggle"
              className={`text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-md border ${showArchived ? "bg-slate-800 text-white border-slate-800 hover:bg-slate-700" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}
              title={showArchived ? "Hide archived contracts" : "Include archived contracts (e.g. expired Hapag)"}
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
            <button
              onClick={() => setShowNew(true)}
              data-testid="new-contract-button"
              className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm"
            >
              <Plus size={16} weight="bold" /> New contract
            </button>
          </div>
        }
      />
      {err && (
        <div className="mx-8 mt-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md flex items-start justify-between">
          <span>{err}</span><button onClick={() => setErr("")} className="text-red-500"><X size={14} /></button>
        </div>
      )}

      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading && <div className="col-span-full"><div className="indus-bar w-1/3" /></div>}
        {!loading && list.length === 0 && (
          <div className="col-span-full bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
            <Handshake size={40} weight="duotone" className="mx-auto text-slate-300 mb-3" />
            <div className="font-semibold text-ps-deep">No contracts yet</div>
            <div className="text-sm mt-1">Set up a recurring customer (e.g. De Heus → Kumasi) with a fixed per-trip rate so Seth can keep a weekly tally.</div>
          </div>
        )}
        {list.map((c) => (
          <button
            key={c.id}
            onClick={() => setOpenId(c.id)}
            data-testid={`contract-card-${c.name}`}
            className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-ps-blue hover:shadow-md transition-all group"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">{c.contract_destination || "Contract"}</div>
                <h3 className="font-display font-extrabold text-2xl text-ps-deep tracking-tight">{c.name}</h3>
              </div>
              {c.is_archived ? (
                <div className="bg-slate-200 text-slate-700 rounded-md px-2 py-1 text-[10px] uppercase tracking-wider font-bold">Archived</div>
              ) : (
                <div className="bg-lime-100 text-lime-900 rounded-md px-2 py-1 text-[10px] uppercase tracking-wider font-bold">Contract</div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Trips YTD</div>
                <div className="font-display font-extrabold text-3xl text-ps-deep tabular">{c.trips_ytd}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Revenue YTD</div>
                <div className="font-mono-data font-semibold text-xl text-lime-700 tabular">{fmtMoney(c.revenue_ytd, c.rate_currency)}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 flex items-center gap-2">
              <Coins size={12} className="text-amber-600" />
              {fmtMoney(c.rate_per_trip || 0, c.rate_currency)} per trip
              {c.contract_destination && <><span className="mx-1">·</span><MapPin size={12} className="text-ps-blue" />{c.contract_destination}</>}
            </div>
          </button>
        ))}
      </div>

      {openId && <ContractDrawer customerId={openId} onClose={() => { setOpenId(null); load(); }} />}
      {showNew && <NewContractModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

// ---------- Drawer: weekly tally + year heatmap ----------
function ContractDrawer({ customerId, onClose }) {
  const [customer, setCustomer] = useState(null);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [trips, setTrips] = useState([]);
  const [yearData, setYearData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [showLanesManager, setShowLanesManager] = useState(false);
  const [adding, setAdding] = useState(null);  // {date, truck, driver, fuel, tonnes, lane_id}
  const [batchOpen, setBatchOpen] = useState(false);  // multi-truck batch dispatch modal
  const [reconciling, setReconciling] = useState(null);  // activity being reconciled
  const year = weekStart.getFullYear();

  const reload = async () => {
    setLoading(true);
    setErr("");
    try {
      const from = ymd(weekStart);
      const to = ymd(addDays(weekStart, 6));
      const [c, t, y, dr, tr, lns] = await Promise.all([
        api.get(`/customers/${customerId}`).then((r) => r.data),
        api.get(`/contracts/${customerId}/trips`, { params: { from, to } }),
        api.get(`/contracts/${customerId}/year-summary`, { params: { year } }),
        api.get(`/drivers`).catch(() => ({ data: [] })),
        api.get(`/trucks`).catch(() => ({ data: [] })),
        api.get(`/customers/${customerId}/lanes`).catch(() => ({ data: [] })),
      ]);
      setCustomer(c);
      setTrips(t.data);
      setYearData(y.data);
      setDrivers(dr.data || []);
      setTrucks(tr.data || []);
      setLanes(lns.data || []);
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [customerId, weekStart.getTime()]);

  const tripsByDay = useMemo(() => {
    const m = {};
    for (let i = 0; i < 7; i += 1) m[ymd(addDays(weekStart, i))] = [];
    for (const t of trips) {
      const k = (t.date || "").slice(0, 10);
      if (m[k] !== undefined) m[k].push(t);
    }
    return m;
  }, [trips, weekStart]);

  const weekTotal = trips.length;
  const isPerTonne = (customer?.default_billing_mode || "per_trip") === "per_tonne";
  const weekRevenue = isPerTonne
    ? trips.reduce((s, t) => s + ((t.tonnes_estimated || t.tonnes_actual || 0) * (customer?.rate_per_tonne || 0)), 0)
    : weekTotal * (customer?.rate_per_trip || 0);
  const weekTonnes = trips.reduce((s, t) => s + (t.tonnes_actual || t.tonnes_estimated || 0), 0);
  const weekFuel = trips.reduce((s, t) => s + (t.fuel_requested_l || 0), 0);
  const wk = isoWeek(weekStart);

  const submitAdd = async () => {
    if (!adding) return;
    const veh = (adding.truck || "").trim().toUpperCase();
    if (!veh) { setErr("Truck is required"); return; }
    const pickedLane = lanes.find((l) => l.id === adding.lane_id);
    const billing_mode = pickedLane?.billing_mode || customer?.default_billing_mode || "per_trip";
    try {
      const body = {
        date: adding.date,
        vehicle_number: veh,
        driver_name: (adding.driver || "").trim() || undefined,
        fuel_l: adding.fuel ? parseFloat(adding.fuel) : undefined,
      };
      if (adding.lane_id) body.lane_id = adding.lane_id;
      // Only send destination if the user typed something — otherwise let the backend
      // fall back to the lane / contract default. Same for origin (currently no UI field
      // for it — placeholder for future expansion).
      if (adding.destination && adding.destination.trim()) body.destination = adding.destination.trim();
      if (adding.origin && adding.origin.trim()) body.origin = adding.origin.trim();
      if (billing_mode === "per_tonne" && adding.tonnes !== "" && adding.tonnes != null) {
        body.tonnes_estimated = parseFloat(adding.tonnes);
      }
      await api.post(`/contracts/${customerId}/trips`, body);
      setAdding(null);
      await reload();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };
  const removeTrip = async (id) => {
    try { await api.delete(`/contracts/trips/${id}`); await reload(); }
    catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/50 flex justify-end" onClick={onClose} data-testid="contract-drawer">
      <div className="bg-white w-full max-w-5xl h-full overflow-y-auto border-l border-slate-200" onClick={(e) => e.stopPropagation()}>
        {loading || !customer ? (
          <div className="p-8"><div className="indus-bar w-1/3" /></div>
        ) : (
          <>
            <div className="px-6 py-5 border-b border-slate-200 sticky top-0 bg-white z-10">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Contract customer</div>
                  <h2 className="font-display font-extrabold text-3xl tracking-tight text-ps-deep">{customer.name}</h2>
                  <div className="text-sm text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
                    <span>{fmtMoney(customer.rate_per_trip || 0, customer.rate_currency)} per trip</span>
                    {customer.contract_destination && <span className="flex items-center gap-1"><MapPin size={12} /> {customer.contract_destination}</span>}
                    <button
                      onClick={() => setShowLanesManager(true)}
                      data-testid="contract-manage-lanes"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.15em] border border-ps-blue text-ps-blue rounded-md hover:bg-sky-50"
                      title="Manage service lanes for this contract customer"
                    >
                      <Stack size={12} weight="bold" />
                      {lanes.length > 0 ? <>{lanes.length} lane{lanes.length === 1 ? "" : "s"}</> : <>Add lanes</>}
                    </button>
                    <button
                      onClick={() => setBatchOpen(true)}
                      data-testid="contract-batch-dispatch"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.15em] border border-lime-600 text-lime-700 rounded-md hover:bg-lime-50"
                      title="Dispatch several trucks at once for today (e.g. an evacuation batch)"
                    >
                      <TruckIcon size={12} weight="bold" />
                      Batch dispatch
                    </button>
                    <button
                      onClick={async () => {
                        const targetArchived = !customer.is_archived;
                        const ok = window.confirm(targetArchived
                          ? `Archive '${customer.name}'?\n\nThe contract will disappear from the Contracts list (toggle "Show archived" to see it). Lanes, trip history and invoices stay intact.`
                          : `Restore '${customer.name}' from the archive?`);
                        if (!ok) return;
                        try {
                          await api.patch(`/customers/${customer.id}`, { is_archived: targetArchived });
                          onClose();
                        } catch (e) {
                          setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                        }
                      }}
                      data-testid="contract-archive-toggle"
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.15em] border rounded-md ${customer.is_archived ? "border-slate-400 text-slate-500 hover:bg-slate-50" : "border-red-400 text-red-600 hover:bg-red-50"}`}
                      title={customer.is_archived ? "Restore this contract from the archive" : "Archive this contract (e.g. expired Hapag) — history is preserved"}
                    >
                      {customer.is_archived ? "Unarchive" : "Archive"}
                    </button>
                  </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"><X size={20} /></button>
              </div>
            </div>

            {err && (
              <div className="mx-6 mt-3 border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md">{err}</div>
            )}

            {/* Week navigator */}
            <div className="px-6 pt-5 pb-3 flex items-center gap-3">
              <button onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="week-prev" className="border border-slate-300 rounded-md p-2 hover:bg-slate-50"><CaretLeft size={14} weight="bold" /></button>
              <div className="flex-1 text-center">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Week {wk} · {year}</div>
                <div className="font-display font-extrabold text-xl text-ps-deep tracking-tight">{weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – {addDays(weekStart, 6).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
              </div>
              <button onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="week-next" className="border border-slate-300 rounded-md p-2 hover:bg-slate-50"><CaretRight size={14} weight="bold" /></button>
              <button onClick={() => setWeekStart(mondayOf(new Date()))} className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep px-3 py-2 border border-ps-blue rounded-md hover:bg-sky-50">This week</button>
            </div>

            {/* 7 day cards */}
            <div className="px-6 pb-5">
              <div className="flex items-center gap-3 mb-3 text-[10px] uppercase tracking-[0.15em] text-slate-500" data-testid="status-legend">
                <span className="font-bold">Status:</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500"/>Approval pending</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500"/>Reconcile pending</span>
                <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-600"/>Completed</span>
              </div>
              <div className="grid grid-cols-7 gap-2" data-testid="week-grid">
                {DAY_LABELS.map((dl, i) => {
                  const date = addDays(weekStart, i);
                  const dateStr = ymd(date);
                  const dayTrips = tripsByDay[dateStr] || [];
                  const isToday = ymd(new Date()) === dateStr;
                  const isWknd = i >= 5;
                  return (
                    <div
                      key={dl}
                      className={`border rounded-md ${isToday ? "border-ps-blue border-2" : "border-slate-200"} ${isWknd ? "bg-slate-50/50" : "bg-white"} flex flex-col`}
                      data-testid={`day-card-${dateStr}`}
                    >
                      <div className="px-2 pt-2 pb-1 flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{dl}</div>
                        <div className="text-[10px] font-mono-data text-slate-400">{pad(date.getDate())}/{pad(date.getMonth() + 1)}</div>
                      </div>
                      <div className={`px-2 py-2 text-center ${dayTrips.length > 0 ? "bg-lime-50" : ""}`}>
                        <div className={`font-display font-extrabold text-3xl ${dayTrips.length > 0 ? "text-ps-deep" : "text-slate-300"}`}>{dayTrips.length}</div>
                        <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-0.5">trip{dayTrips.length === 1 ? "" : "s"}</div>
                      </div>
                      <div className="px-2 py-1 space-y-1 flex-1">
                        {dayTrips.map((t) => {
                          const phase = tripPhase(t.status);
                          const ps = PHASE_STYLES[phase];
                          return (
                          <div
                            key={t.id}
                            onClick={() => setReconciling(t)}
                            className={`text-[10px] border rounded px-1.5 py-1 cursor-pointer transition-colors ${ps.ring}`}
                            data-testid={`trip-chip-${t.id}`}
                            data-phase={phase}
                            title={`${ps.label} — ${t.status.toUpperCase()}`}
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span className="font-mono-data font-bold text-ps-blue truncate flex-1">{t.vehicle_number}</span>
                              <StatusDot status={t.status} />
                              {!["issued", "completed"].includes(t.status) && (
                                <button onClick={(e) => { e.stopPropagation(); removeTrip(t.id); }} className="text-slate-400 hover:text-red-500 ml-0.5" data-testid={`trip-remove-${t.id}`}><X size={9} /></button>
                              )}
                            </div>
                            {t.driver_name && <div className="text-[9px] text-slate-500 truncate">{t.driver_name}</div>}
                            <div className="text-[9px] text-slate-400 tabular flex items-center gap-1">
                              <span>{t.fuel_requested_l || 0}L</span>
                              {(t.tonnes_actual || t.tonnes_estimated) ? <span>· {(t.tonnes_actual || t.tonnes_estimated)}t</span> : null}
                              {t.billable_amount > 0 ? <span className="text-lime-700 font-semibold">· {t.billable_amount.toFixed(0)} {t.rate_currency_snapshot}</span> : null}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => setAdding({ date: dateStr, truck: "", driver: "", fuel: customer.default_fuel_per_trip_l || 250, tonnes: "", lane_id: "" })}
                        data-testid={`day-add-${dateStr}`}
                        className="m-1.5 px-2 py-1.5 rounded bg-ps-blue/10 hover:bg-ps-blue/20 text-ps-blue text-[10px] uppercase tracking-wider font-bold flex items-center justify-center gap-1"
                        title="Add a trip"
                      >
                        <Plus size={11} weight="bold" /> Add trip
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Week summary */}
              <div className={`mt-4 bg-slate-50 border border-slate-200 rounded-md p-4 grid ${isPerTonne ? "grid-cols-5" : "grid-cols-4"} gap-4`}>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Week total</div>
                  <div className="font-display font-extrabold text-2xl text-ps-deep tabular">{weekTotal} trips</div>
                </div>
                {isPerTonne && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Tonnes</div>
                    <div className="font-display font-extrabold text-2xl text-ps-deep tabular">{weekTonnes.toFixed(1)} t</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Revenue</div>
                  <div className="font-mono-data font-semibold text-2xl text-lime-700 tabular">{fmtMoney(weekRevenue, customer.rate_currency)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Fuel requested</div>
                  <div className="font-mono-data font-semibold text-2xl text-ps-deep tabular">{weekFuel.toFixed(0)} L</div>
                </div>
                <div className="text-[10px] text-slate-500 italic flex items-end">{isPerTonne ? "Per-tonne · click a trip chip to record actual tonnes + waiting days." : "Click a trip chip to mark trips actually done."}</div>
              </div>
            </div>

            {/* Year heatmap */}
            {yearData && (
              <div className="px-6 pb-8 border-t border-slate-100 pt-5">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-display font-extrabold text-lg tracking-tight text-ps-deep">{year} year overview</h3>
                  <div className="text-sm text-slate-600">{yearData.total_trips} trips{yearData.total_tonnes ? <> · <span className="font-mono-data text-ps-deep">{yearData.total_tonnes} t</span></> : null} · <span className="text-lime-700 font-semibold">{fmtMoney(yearData.total_revenue, yearData.rate_currency)}</span></div>
                </div>
                <YearHeatmap
                  weeks={yearData.weeks}
                  year={year}
                  currentWeek={wk}
                  onWeekClick={(w) => {
                    const jan4 = new Date(year, 0, 4);
                    const mon = mondayOf(jan4);
                    setWeekStart(addDays(mon, (w - 1) * 7));
                  }}
                />
              </div>
            )}
          </>
        )}

        {/* Add-trip modal */}
        {adding && (
          <div className="fixed inset-0 z-[60] bg-slate-900/50 grid place-items-center p-4" onClick={() => setAdding(null)} data-testid="contract-trip-add-modal">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-slate-200 shrink-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">{customer?.name} · {adding.date}</div>
                <h2 className="font-display font-extrabold text-xl text-ps-deep tracking-tight">Add trip</h2>
              </div>
              <div className="p-5 space-y-3 flex-1 overflow-y-auto">
                {lanes.length > 0 && (
                  <div data-testid="contract-lane-picker">
                    <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Service lane</label>
                    <div className="space-y-1.5">
                      {lanes.map((lane) => {
                        const active = adding.lane_id === lane.id;
                        return (
                          <button
                            key={lane.id}
                            type="button"
                            data-testid={`contract-lane-${lane.id}`}
                            onClick={() => setAdding({
                              ...adding,
                              lane_id: active ? "" : lane.id,
                              fuel: (!active && lane.default_fuel_per_trip_l) ? String(lane.default_fuel_per_trip_l) : adding.fuel,
                            })}
                            className={`w-full text-left border rounded p-2 transition-colors ${active ? "border-ps-blue bg-sky-50" : "border-slate-200 hover:border-ps-blue hover:bg-sky-50/50"}`}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`w-2 h-2 rounded-full ${active ? "bg-ps-blue" : "bg-slate-300"}`} />
                              <span className="font-semibold text-ps-deep text-sm">{lane.name}</span>
                              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${lane.billing_mode === "per_tonne" ? "bg-amber-50 text-ps-orange border-amber-200" : "bg-sky-50 text-ps-blue border-sky-200"}`}>{lane.billing_mode === "per_tonne" ? "per tonne" : "per trip"}</span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 ml-4 font-mono-data">
                              {lane.rate_per_unit ? <>{lane.rate_per_unit} {lane.rate_currency} / {lane.billing_mode === "per_tonne" ? "t" : "trip"}</> : <span className="text-amber-700">no rate</span>}
                              {lane.default_fuel_per_trip_l ? <> · {lane.default_fuel_per_trip_l} L</> : null}
                              {(lane.origin || lane.destination) ? <> · {[lane.origin, lane.destination].filter(Boolean).join(" → ")}</> : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {adding.lane_id && (
                      <button type="button" onClick={() => setAdding({ ...adding, lane_id: "" })} className="text-[11px] text-slate-500 hover:text-ps-deep underline mt-1">Clear lane</button>
                    )}
                  </div>
                )}
                <Lbl label="Truck *">
                  <input
                    type="text"
                    autoFocus
                    value={adding.truck}
                    onChange={(e) => setAdding({ ...adding, truck: e.target.value.toUpperCase() })}
                    list="contract-trucks-options"
                    data-testid="contract-add-truck"
                    placeholder="GT 9160"
                    className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data uppercase rounded"
                  />
                  <datalist id="contract-trucks-options">
                    {trucks.map((t) => <option key={t.id} value={t.vehicle_number} />)}
                  </datalist>
                </Lbl>
                <Lbl label="Driver">
                  <input
                    type="text"
                    value={adding.driver}
                    onChange={(e) => setAdding({ ...adding, driver: e.target.value.toUpperCase() })}
                    list="contract-drivers-options"
                    data-testid="contract-add-driver"
                    placeholder="Kwame Asante"
                    className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data uppercase rounded"
                  />
                  <datalist id="contract-drivers-options">
                    {drivers.map((d) => <option key={d.id} value={d.name} />)}
                  </datalist>
                </Lbl>
                <Lbl label={`Fuel (L) — default ${customer?.default_fuel_per_trip_l || 250} L`}>
                  <input
                    type="number" min="0" step="1"
                    value={adding.fuel}
                    onChange={(e) => setAdding({ ...adding, fuel: e.target.value })}
                    data-testid="contract-add-fuel"
                    className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data text-right rounded"
                  />
                </Lbl>
                <Lbl label="Destination (optional)">
                  <input
                    type="text"
                    value={adding.destination || ""}
                    onChange={(e) => setAdding({ ...adding, destination: e.target.value })}
                    list="contract-destinations-options"
                    data-testid="contract-add-destination"
                    placeholder={
                      (lanes.find((l) => l.id === adding.lane_id)?.destination)
                      || customer?.contract_destination
                      || "e.g. GPHA, MPS, Kumasi"
                    }
                    className="w-full border border-slate-300 px-3 py-2 text-sm rounded"
                  />
                  <datalist id="contract-destinations-options">
                    {[...new Set(lanes.map((l) => l.destination).filter(Boolean))].map((d) => (
                      <option key={d} value={d} />
                    ))}
                    {customer?.contract_destination && <option value={customer.contract_destination} />}
                  </datalist>
                </Lbl>
                {(() => {
                  const pickedLane = lanes.find((l) => l.id === adding.lane_id);
                  const effMode = pickedLane?.billing_mode || customer?.default_billing_mode || "per_trip";
                  const effRate = pickedLane?.rate_per_unit ?? (effMode === "per_tonne" ? customer?.rate_per_tonne : customer?.rate_per_trip);
                  const effCcy = pickedLane?.rate_currency || customer?.rate_currency || "GHS";
                  return (
                    <>
                      {effMode === "per_tonne" && (
                        <Lbl label={`Tonnes (estimated) — ${effRate ? `${effRate} ${effCcy}/t` : "no rate set"}`}>
                          <input
                            type="number" min="0" step="0.01"
                            value={adding.tonnes}
                            onChange={(e) => setAdding({ ...adding, tonnes: e.target.value })}
                            data-testid="contract-add-tonnes"
                            placeholder="e.g. 25"
                            className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data text-right rounded"
                          />
                        </Lbl>
                      )}
                      {effMode === "per_trip" && effRate > 0 && (
                        <div className="text-[11px] text-slate-600 bg-sky-50 border border-sky-200 rounded px-2 py-1.5">
                          Billable: <span className="font-mono-data font-semibold text-ps-deep">{effRate} {effCcy}</span> / trip
                        </div>
                      )}
                    </>
                  );
                })()}
                <div className="text-[11px] text-slate-500 italic">This creates a Trip → Seth allocates → Collins issues, same as any other.</div>
              </div>
              <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 rounded-b-xl shrink-0">
                <button onClick={() => setAdding(null)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100 rounded">Cancel</button>
                <button onClick={submitAdd} data-testid="contract-add-submit" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep rounded">Add trip</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showLanesManager && customer && (
        <LanesManager
          customer={customer}
          onClose={() => { setShowLanesManager(false); reload(); }}
        />
      )}
      {reconciling && (
        <ReconcileTripModal
          activity={reconciling}
          onClose={() => setReconciling(null)}
          onSaved={async () => { setReconciling(null); await reload(); }}
        />
      )}
      {batchOpen && customer && (
        <BatchDispatchModal
          customer={customer}
          lanes={lanes}
          trucks={trucks}
          drivers={drivers}
          onClose={() => setBatchOpen(false)}
          onSaved={async () => { setBatchOpen(false); await reload(); }}
        />
      )}
    </div>
  );
}

// ---------- Batch dispatch modal — multi-truck nomination for one day ----------
// Replaces the old Evacuations workflow: pick a date + lane, tick the trucks doing the run,
// hit Dispatch — we create one /contracts/{id}/trips POST per truck so each appears in the
// weekly calendar above and follows the normal Seth → Collins approval chain.
function BatchDispatchModal({ customer, lanes, trucks, drivers, onClose, onSaved }) {
  const today = ymd(new Date());
  const [date, setDate] = useState(today);
  const [laneId, setLaneId] = useState(() => (lanes[0]?.id || ""));
  const [selectedTrucks, setSelectedTrucks] = useState([]);   // [vehicle_number]
  const [driverMap, setDriverMap] = useState({});             // {vehicle_number: driver_name}
  const [perTruckFuel, setPerTruckFuel] = useState(String(lanes[0]?.default_fuel_per_trip_l || customer.default_fuel_per_trip_l || 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progress, setProgress] = useState(null);  // {done, total} while dispatching

  // Country lock — Hapag/Arkas in particular are GH-only (per ops 2026-06-25)
  const allowedCountry = customer.country || "GH";
  const filteredTrucks = useMemo(
    () => trucks.filter((t) => (t.country || "GH") === allowedCountry).sort((a, b) => a.vehicle_number.localeCompare(b.vehicle_number)),
    [trucks, allowedCountry]
  );

  const pickedLane = lanes.find((l) => l.id === laneId);
  const billingMode = pickedLane?.billing_mode || customer.default_billing_mode || "per_trip";
  const ratePerTrip = pickedLane?.rate_per_unit ?? customer.rate_per_trip ?? 0;
  const ccy = pickedLane?.rate_currency || customer.rate_currency || "GHS";
  const estRevenue = billingMode === "per_trip" ? selectedTrucks.length * ratePerTrip : 0;
  const totalFuel = selectedTrucks.length * (parseFloat(perTruckFuel) || 0);

  const toggleTruck = (plate) => {
    setSelectedTrucks((prev) => (prev.includes(plate) ? prev.filter((p) => p !== plate) : [...prev, plate]));
    if (!selectedTrucks.includes(plate)) {
      // auto-fill driver from the truck record if available
      const tk = filteredTrucks.find((t) => t.vehicle_number === plate);
      if (tk && tk.assigned_driver_name && !driverMap[plate]) {
        setDriverMap((m) => ({ ...m, [plate]: tk.assigned_driver_name }));
      }
    }
  };

  const selectAll = () => setSelectedTrucks(filteredTrucks.map((t) => t.vehicle_number));
  const clearAll = () => setSelectedTrucks([]);

  const submit = async (e) => {
    e.preventDefault();
    if (selectedTrucks.length === 0) { setErr("Pick at least one truck"); return; }
    setBusy(true);
    setErr("");
    setProgress({ done: 0, total: selectedTrucks.length });
    let failed = [];
    for (let i = 0; i < selectedTrucks.length; i += 1) {
      const veh = selectedTrucks[i];
      const body = {
        date,
        vehicle_number: veh,
        driver_name: (driverMap[veh] || "").trim() || undefined,
        fuel_l: parseFloat(perTruckFuel) || 0,
      };
      if (laneId) body.lane_id = laneId;
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/contracts/${customer.id}/trips`, body);
      } catch (e2) {
        failed.push({ veh, msg: formatApiErrorDetail(e2?.response?.data?.detail) || e2.message });
      }
      setProgress({ done: i + 1, total: selectedTrucks.length });
    }
    setBusy(false);
    if (failed.length === 0) {
      onSaved();
    } else {
      setErr(`Dispatched ${selectedTrucks.length - failed.length}/${selectedTrucks.length}. Failed: ${failed.map((f) => `${f.veh} (${f.msg})`).join("; ")}`);
      setProgress(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 grid place-items-center p-4" onClick={() => !busy && onClose()} data-testid="contract-batch-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-2xl border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-lime-700 font-semibold">{customer.name} · batch dispatch</div>
          <h2 className="font-display font-extrabold text-2xl text-ps-deep tracking-tight">Dispatch trucks for today</h2>
          <p className="text-xs text-slate-500 mt-1">Pick the trucks doing the run — one fuel request per truck is created, the operator can then send the batch to {`Collins`} for approval as usual.</p>
        </div>

        <div className="p-5 space-y-4 flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Date">
              <input
                type="date" required
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="batch-date"
                className="w-full border border-slate-300 px-3 py-2 text-sm rounded font-mono-data"
              />
            </Lbl>
            <Lbl label="Lane">
              <select
                value={laneId}
                onChange={(e) => {
                  setLaneId(e.target.value);
                  const lane = lanes.find((l) => l.id === e.target.value);
                  if (lane?.default_fuel_per_trip_l) setPerTruckFuel(String(lane.default_fuel_per_trip_l));
                }}
                data-testid="batch-lane"
                className="w-full border border-slate-300 px-3 py-2 text-sm rounded bg-white"
              >
                <option value="">— No lane —</option>
                {lanes.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name} · {l.rate_per_unit ? `${l.rate_per_unit} ${l.rate_currency}/${l.billing_mode === "per_tonne" ? "t" : "trip"}` : "no rate"}
                  </option>
                ))}
              </select>
            </Lbl>
          </div>

          <Lbl label={`Fuel per truck (L) — applied to all selected`}>
            <input
              type="number" min="0" step="1"
              value={perTruckFuel}
              onChange={(e) => setPerTruckFuel(e.target.value)}
              data-testid="batch-fuel"
              className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data text-right rounded"
            />
          </Lbl>

          <div className="border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">Trucks · {filteredTrucks.length} available ({allowedCountry})</div>
                <div className="text-xs text-slate-500 mt-0.5">{selectedTrucks.length} selected</div>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={selectAll} data-testid="batch-select-all" className="text-[11px] uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep px-2 py-1 border border-slate-200 rounded hover:bg-slate-50">All</button>
                <button type="button" onClick={clearAll} data-testid="batch-clear-all" className="text-[11px] uppercase tracking-wider font-bold text-slate-500 hover:text-ps-deep px-2 py-1 border border-slate-200 rounded hover:bg-slate-50">None</button>
              </div>
            </div>
            {filteredTrucks.length === 0 ? (
              <div className="text-sm text-slate-500 italic border border-dashed border-slate-300 rounded p-3 text-center">No {allowedCountry} trucks registered. Add some on the Trucks page first.</div>
            ) : (
              <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto" data-testid="batch-truck-grid">
                {filteredTrucks.map((tk) => {
                  const checked = selectedTrucks.includes(tk.vehicle_number);
                  return (
                    <label
                      key={tk.vehicle_number}
                      className={`flex items-center gap-2 border rounded px-2 py-1.5 cursor-pointer transition-colors ${checked ? "border-ps-blue bg-sky-50" : "border-slate-200 hover:border-ps-blue/50 hover:bg-slate-50"}`}
                      data-testid={`batch-truck-${tk.vehicle_number}`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleTruck(tk.vehicle_number)} className="accent-ps-blue" />
                      <span className="font-mono-data font-bold text-sm text-ps-deep">{tk.vehicle_number}</span>
                      {checked && (
                        <input
                          type="text"
                          value={driverMap[tk.vehicle_number] || ""}
                          onChange={(e) => setDriverMap((m) => ({ ...m, [tk.vehicle_number]: e.target.value.toUpperCase() }))}
                          placeholder="driver"
                          list={`drivers-${tk.vehicle_number}`}
                          data-testid={`batch-driver-${tk.vehicle_number}`}
                          className="flex-1 min-w-0 text-[11px] border-b border-slate-200 px-1 py-0.5 font-mono-data uppercase focus:border-ps-blue focus:outline-none"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <datalist id={`drivers-${tk.vehicle_number}`}>
                        {drivers.map((d) => <option key={d.id} value={d.name} />)}
                      </datalist>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {selectedTrucks.length > 0 && (
            <div className="bg-lime-50 border border-lime-200 rounded p-3 text-xs space-y-1" data-testid="batch-summary">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-lime-800 uppercase tracking-wider text-[10px]">Batch summary:</span>
                <span><span className="font-mono-data font-bold text-ps-deep">{selectedTrucks.length}</span> truck{selectedTrucks.length === 1 ? "" : "s"}</span>
                <span>· total fuel <span className="font-mono-data font-bold text-ps-deep">{totalFuel.toFixed(0)} L</span></span>
                {estRevenue > 0 && <span>· est. revenue <span className="font-mono-data font-bold text-lime-700">{fmtMoney(estRevenue, ccy)}</span></span>}
              </div>
              <div className="text-[10px] text-slate-500">Creates {selectedTrucks.length} fuel request{selectedTrucks.length === 1 ? "" : "s"} in status <span className="font-bold uppercase">requested</span> — Collins still needs to approve each one before fuel issues.</div>
            </div>
          )}

          {err && <div className="text-sm text-ps-red bg-red-50 border border-red-200 rounded p-2" data-testid="batch-error">{err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-between items-center gap-2 shrink-0">
          {progress ? (
            <div className="text-xs text-slate-600 font-mono-data" data-testid="batch-progress">
              Dispatching {progress.done}/{progress.total}…
            </div>
          ) : (
            <div className="text-[11px] text-slate-500">All trucks dispatched as separate fuel requests.</div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-xs uppercase tracking-[0.15em] border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={busy || selectedTrucks.length === 0} data-testid="batch-submit" className="px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold bg-lime-600 text-white rounded hover:bg-lime-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              <TruckIcon size={14} weight="bold" />
              {busy ? "Dispatching…" : `Dispatch ${selectedTrucks.length || ""}`}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function ReconcileTripModal({ activity, onClose, onSaved }) {
  const isPerTonne = activity.billing_mode === "per_tonne";
  const cap = activity.trips_planned || 1;
  const [tripsDone, setTripsDone] = useState(String(activity.trips_done || 1));
  const [tonnesActual, setTonnesActual] = useState(activity.tonnes_actual ? String(activity.tonnes_actual) : (activity.tonnes_estimated ? String(activity.tonnes_estimated) : ""));
  const [waitingActual, setWaitingActual] = useState(activity.waiting_days_actual ? String(activity.waiting_days_actual) : (activity.waiting_days_estimated ? String(activity.waiting_days_estimated) : ""));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const rate = parseFloat(activity.rate_per_unit_snapshot) || 0;
  const wdRate = parseFloat(activity.waiting_day_rate_snapshot) || 0;
  const ccy = activity.rate_currency_snapshot || "GHS";

  const blocked = ["requested", "rejected", "cancelled"].includes(activity.status);
  const completed = activity.status === "completed" && activity.billing_status !== "draft";

  const qty = isPerTonne ? parseFloat(tonnesActual || 0) : parseInt(tripsDone || 0, 10);
  const wd = parseFloat(waitingActual || 0);
  const billable = rate * qty + wdRate * wd;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const body = { trips_done: parseInt(tripsDone, 10) || 1 };
      if (tonnesActual !== "") body.tonnes_actual = parseFloat(tonnesActual);
      if (waitingActual !== "") body.waiting_days_actual = parseFloat(waitingActual);
      await api.patch(`/activities/${activity.id}/trips-done`, body);
      await onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/50 grid place-items-center p-4" onClick={onClose} data-testid="reconcile-trip-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-2xl w-full max-w-md border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3 shrink-0">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">{activity.client} · {(activity.date || "").slice(0, 10)}</div>
            <h2 className="font-display font-extrabold text-2xl text-ps-deep tracking-tight">Reconcile trip</h2>
            <div className="text-xs text-slate-500 mt-1 font-mono-data">
              {activity.vehicle_number}{activity.driver_name ? ` · ${activity.driver_name}` : ""}
            </div>
            {activity.lane_name_snapshot && (
              <div className="text-[11px] text-ps-orange font-semibold mt-0.5">{activity.lane_name_snapshot}</div>
            )}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-ps-deep p-1" data-testid="reconcile-close"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3 flex-1 overflow-y-auto">
          {/* State banner */}
          <div className={`px-3 py-2 rounded text-xs ${activity.status === "requested" ? "bg-amber-50 border border-amber-200 text-amber-800" : activity.status === "approved" ? "bg-sky-50 border border-sky-200 text-ps-blue" : activity.status === "issued" || activity.status === "awaiting_waybill" ? "bg-lime-50 border border-lime-200 text-lime-800" : activity.status === "completed" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-slate-50 border border-slate-200 text-slate-700"}`}>
            Status: <span className="font-bold uppercase">{activity.status}</span>
            {activity.fuel_approved_l > 0 && <span> · Fuel approved <span className="font-mono-data">{activity.fuel_approved_l} L</span></span>}
            {activity.fuel_credit_applied_l > 0 && <span className="text-amber-700"> · credit {activity.fuel_credit_applied_l} L</span>}
            {blocked && <div className="mt-1 text-[11px]">This trip is not ready for reconciliation — wait for Seth → Collins approval first.</div>}
          </div>

          {!blocked && (
            <>
              <ReconcileCorrectionNote activity={activity} />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Trips actually completed (cap {cap})</label>
                <input
                  type="number" min="1" max={cap} step="1"
                  value={tripsDone}
                  onChange={(e) => setTripsDone(e.target.value)}
                  data-testid="reconcile-trips-done"
                  className="w-full border border-slate-300 px-3 py-2 text-2xl font-mono-data tabular text-center font-bold text-ps-deep focus:border-ps-blue rounded"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Fuel: <span className="font-mono-data">{activity.fuel_approved_l || 0}</span> L ÷ <span className="font-mono-data">{activity.litres_per_trip || 10}</span> L/trip = cap {cap}.
                  Unused fuel credits back to the truck for the next allocation.
                </div>
              </div>

              {isPerTonne && (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Tonnes actually carried</label>
                  <input
                    type="number" step="0.01" min="0"
                    value={tonnesActual}
                    onChange={(e) => setTonnesActual(e.target.value)}
                    data-testid="reconcile-tonnes-actual"
                    placeholder={activity.tonnes_estimated ? `estimate ${activity.tonnes_estimated} t` : "0"}
                    className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data focus:border-ps-blue rounded"
                  />
                </div>
              )}

              {wdRate > 0 && (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Waiting days</label>
                  <input
                    type="number" step="0.5" min="0"
                    value={waitingActual}
                    onChange={(e) => setWaitingActual(e.target.value)}
                    data-testid="reconcile-waiting-actual"
                    placeholder="0"
                    className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data focus:border-ps-blue rounded"
                  />
                </div>
              )}

              {(rate > 0 || wdRate > 0) && (
                <div className="bg-lime-50 border border-lime-200 rounded p-2 text-xs" data-testid="reconcile-billable-preview">
                  Rate: <span className="font-mono-data font-semibold text-ps-deep">{rate} {ccy}</span> / {isPerTonne ? "tonne" : "trip"}
                  {wdRate > 0 && <> · waiting <span className="font-mono-data font-semibold text-ps-deep">{wdRate} {ccy}</span>/day</>}
                  <div className="mt-1">Billable: <span className="font-mono-data font-bold text-lime-700 text-base">{billable.toFixed(2)} {ccy}</span></div>
                  {completed && <div className="text-[10px] text-slate-500 mt-0.5">Currently <span className="font-mono-data">{activity.billable_amount} {ccy}</span> — re-saving will recompute.</div>}
                </div>
              )}
            </>
          )}

          {err && <div className="text-sm text-ps-red bg-red-50 border border-red-200 rounded p-2" data-testid="reconcile-error">{err}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2 shrink-0">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs uppercase tracking-[0.15em] border border-slate-300 rounded hover:bg-slate-100">Close</button>
          {!blocked && (
            <button type="submit" disabled={saving} data-testid="reconcile-submit" className="px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold bg-ps-blue text-white rounded hover:bg-ps-deep disabled:opacity-60">
              {saving ? "Saving…" : "Save reconciliation"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function tripPhase(status) {
  // Three-color model requested by ops:
  //  red    = fuel still has to be approved/issued (requested or approved-but-not-issued)
  //  amber  = fuel issued, reconciliation still pending (issued / awaiting_waybill)
  //  green  = completed
  //  rejected/cancelled use a muted slate
  if (status === "requested" || status === "approved") return "red";
  if (status === "issued" || status === "awaiting_waybill") return "amber";
  if (status === "completed") return "green";
  return "muted";
}
const PHASE_STYLES = {
  red:    { dot: "bg-rose-500",   ring: "border-rose-300 bg-rose-50/70   hover:border-rose-500",   label: "Fuel approval pending" },
  amber:  { dot: "bg-amber-500",  ring: "border-amber-300 bg-amber-50/70 hover:border-amber-500",  label: "Reconciliation pending" },
  green:  { dot: "bg-emerald-600",ring: "border-emerald-300 bg-emerald-50/60 hover:border-emerald-500", label: "Completed" },
  muted:  { dot: "bg-slate-400",  ring: "border-slate-300 bg-slate-50",           label: "Rejected / cancelled" },
};

function StatusDot({ status }) {
  const style = PHASE_STYLES[tripPhase(status)];
  return <span className={`inline-block w-2 h-2 rounded-full ${style.dot} shrink-0`} title={style.label} />;
}

function YearHeatmap({ weeks, year, currentWeek, onWeekClick }) {
  const max = Math.max(1, ...weeks.map((w) => w.trips));
  const level = (n) => {
    if (n === 0) return "bg-slate-100";
    const pct = n / max;
    if (pct <= 0.25) return "bg-lime-200";
    if (pct <= 0.5)  return "bg-lime-400";
    if (pct <= 0.75) return "bg-lime-500";
    return "bg-lime-600";
  };
  return (
    <div data-testid="year-heatmap">
      <div className="grid grid-cols-[repeat(53,1fr)] gap-0.5" style={{ direction: "ltr" }}>
        {weeks.map((w) => (
          <button
            key={w.week}
            onClick={() => onWeekClick(w.week)}
            data-testid={`heatmap-week-${w.week}`}
            title={`Week ${w.week}: ${w.trips} trip${w.trips === 1 ? "" : "s"}`}
            className={`aspect-square rounded-sm ${level(w.trips)} ${w.week === currentWeek ? "ring-2 ring-ps-blue" : "hover:ring-2 hover:ring-ps-blue/50"}`}
          />
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3 text-[10px] text-slate-500">
        <span>Less</span>
        {["bg-slate-100", "bg-lime-200", "bg-lime-400", "bg-lime-500", "bg-lime-600"].map((c) => <div key={c} className={`w-3 h-3 rounded-sm ${c}`} />)}
        <span>More</span>
      </div>
    </div>
  );
}

// ---------- New contract modal ----------
function NewContractModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "",
    contract_destination: "",
    default_billing_mode: "per_trip",
    rate_per_trip: "",
    rate_per_tonne: "",
    waiting_day_rate: "",
    rate_currency: "GHS",
    default_fuel_per_trip_l: "250",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErr("Name is required"); return; }
    setBusy(true); setErr("");
    try {
      const list = await api.get("/customers");
      const existing = list.data.find((c) => c.name.toLowerCase() === form.name.trim().toLowerCase());
      const fuel = form.default_fuel_per_trip_l ? parseFloat(form.default_fuel_per_trip_l) : null;
      const body = {
        is_contract: true,
        default_billing_mode: form.default_billing_mode,
        rate_per_trip:    form.rate_per_trip    ? parseFloat(form.rate_per_trip)    : null,
        rate_per_tonne:   form.rate_per_tonne   ? parseFloat(form.rate_per_tonne)   : null,
        waiting_day_rate: form.waiting_day_rate ? parseFloat(form.waiting_day_rate) : null,
        rate_currency: form.rate_currency,
        contract_destination: form.contract_destination.trim(),
        default_fuel_per_trip_l: fuel,
        default_litres_per_trip: fuel,
        notes: form.notes.trim(),
      };
      if (existing) {
        await api.patch(`/customers/${existing.id}`, body);
      } else {
        await api.post("/customers", { name: form.name.trim(), role: "sender", ...body });
      }
      onSaved();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" onClick={() => !busy && onClose()}>
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-md border border-slate-200 max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Recurring customer</div>
          <h2 className="font-display font-extrabold text-2xl text-ps-deep tracking-tight">New contract</h2>
          <p className="text-xs text-slate-500 mt-1">Promote an existing customer to a contract, or create a new one. Pick per-trip OR per-tonne billing.</p>
        </div>
        <div className="p-5 space-y-3 flex-1 overflow-y-auto">
          <Lbl label="Client name *"><input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-contract-name" className="w-full border border-slate-300 px-3 py-2 text-sm rounded" placeholder="De Heus" /></Lbl>
          <Lbl label="Destination"><input type="text" value={form.contract_destination} onChange={(e) => setForm({ ...form, contract_destination: e.target.value })} data-testid="new-contract-destination" className="w-full border border-slate-300 px-3 py-2 text-sm rounded" placeholder="Kumasi" /></Lbl>

          <div className="border-t border-slate-200 pt-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">Billing</div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { v: "per_trip",  label: "Per trip" },
                { v: "per_tonne", label: "Per tonne" },
              ].map((o) => {
                const active = form.default_billing_mode === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setForm({ ...form, default_billing_mode: o.v })}
                    data-testid={`new-contract-billing-mode-${o.v}`}
                    className={`border rounded-md px-3 py-2 text-sm font-semibold transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
            <Lbl label="Currency">
              <select value={form.rate_currency} onChange={(e) => setForm({ ...form, rate_currency: e.target.value })} data-testid="new-contract-currency" className="w-full border border-slate-300 px-3 py-2 text-sm rounded bg-white">
                {["GHS", "XOF", "USD", "EUR"].map((c) => <option key={c}>{c}</option>)}
              </select>
            </Lbl>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Lbl label={`Rate / trip (${form.rate_currency})`}>
                <input type="number" min="0" step="any" value={form.rate_per_trip} onChange={(e) => setForm({ ...form, rate_per_trip: e.target.value })} data-testid="new-contract-rate" className="w-full border border-slate-300 px-3 py-2 text-sm rounded font-mono-data" placeholder="1200" />
              </Lbl>
              <Lbl label={`Rate / tonne (${form.rate_currency})`}>
                <input type="number" min="0" step="any" value={form.rate_per_tonne} onChange={(e) => setForm({ ...form, rate_per_tonne: e.target.value })} data-testid="new-contract-rate-per-tonne" className="w-full border border-slate-300 px-3 py-2 text-sm rounded font-mono-data" placeholder="45" />
              </Lbl>
            </div>
            <div className="mt-2">
              <Lbl label={`Waiting day rate (${form.rate_currency}/day)`}>
                <input type="number" min="0" step="any" value={form.waiting_day_rate} onChange={(e) => setForm({ ...form, waiting_day_rate: e.target.value })} data-testid="new-contract-waiting-day-rate" className="w-full border border-slate-300 px-3 py-2 text-sm rounded font-mono-data" placeholder="200" />
              </Lbl>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">Whichever mode you pick becomes the default — the operator can still override per-trip at the dispatch screen.</div>
          </div>

          <Lbl label="Default fuel per trip (L) — 80% of trucks use the same">
            <input type="number" min="0" step="1" value={form.default_fuel_per_trip_l} onChange={(e) => setForm({ ...form, default_fuel_per_trip_l: e.target.value })} data-testid="new-contract-fuel" className="w-full border border-slate-300 px-3 py-2 text-sm rounded font-mono-data" placeholder="250" />
          </Lbl>
          <Lbl label="Notes"><input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm rounded" /></Lbl>
          {err && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded">{err}</div>}
        </div>
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 rounded-b-xl shrink-0">
          <button type="button" onClick={onClose} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100 rounded">Cancel</button>
          <button type="submit" disabled={busy} data-testid="new-contract-submit" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep rounded disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function Lbl({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
